const parseDateValue = (value) => {
  if (!value) return null;
  const date = new Date(value);
  const ts = date.getTime();
  return Number.isNaN(ts) ? null : ts;
};

const formatAmount = (amount, currency) => {
  const value = Number(amount || 0);
  const code = currency || "USD";
  return `${code} ${value.toFixed(2)}`;
};

const sumBy = (items, predicate, selector) =>
  items.reduce((total, item) => (predicate(item) ? total + selector(item) : total), 0);

const countBy = (items, predicate) =>
  items.reduce((total, item) => (predicate(item) ? total + 1 : total), 0);

const resolveSubscriptionDate = (subscription) =>
  parseDateValue(subscription.updatedAt || subscription.createdAt);

const resolveSubscriptionInvoiceDate = (invoice) =>
  parseDateValue(invoice.paidDate || invoice.createdDate);

const resolvePaymentDate = (payment) =>
  parseDateValue(payment.createdAt || payment.invoice?.paidDate);

export const buildDashboardKpis = (paymentsHistory, subscriptions, subscriptionInvoices) => {
  const invoiceRevenue = sumBy(
    paymentsHistory,
    (payment) => payment.status === "SUCCESS",
    (payment) => Number(payment.amount || 0)
  );

  const refundedAmount = sumBy(
    paymentsHistory,
    (payment) => payment.status === "REFUNDED",
    (payment) => Number(payment.amount || 0)
  );

  const activeSubscriptions = countBy(
    subscriptions,
    (subscription) => subscription.status === "ACTIVE"
  );

  const canceledSubscriptions = countBy(
    subscriptions,
    (subscription) => subscription.status === "CANCELED"
  );

  const mrr = sumBy(
    subscriptions,
    (subscription) => subscription.status === "ACTIVE",
    (subscription) => Number(subscription.plan?.amount || 0)
  );

  const subscriptionRevenue = sumBy(
    subscriptionInvoices,
    (invoice) => invoice.status === "PAID",
    (invoice) => Number(invoice.amountPaid || 0)
  );

  return {
    invoiceRevenue,
    refundedAmount,
    activeSubscriptions,
    canceledSubscriptions,
    mrr,
    subscriptionRevenue,
  };
};

export const buildAlerts = (paymentsHistory, subscriptions) => {
  const refundedCount = countBy(
    paymentsHistory,
    (payment) => payment.status === "REFUNDED"
  );
  const failedCount = countBy(
    paymentsHistory,
    (payment) => payment.status === "FAILED"
  );
  const canceledCount = countBy(
    subscriptions,
    (subscription) => subscription.status === "CANCELED"
  );

  return [
    { id: "refunded", label: "Refunded payments", count: refundedCount, tone: "orange" },
    { id: "canceled", label: "Canceled subscriptions", count: canceledCount, tone: "orange" },
    { id: "failed", label: "Failed payments", count: failedCount, tone: "red" },
  ];
};

export const buildActivityFeed = (paymentsHistory, subscriptions, subscriptionInvoices) => {
  const activity = [];

  paymentsHistory.forEach((payment) => {
    if (payment.status === "SUCCESS" || payment.status === "REFUNDED") {
      const invoiceNumber = payment.invoice?.invoiceNumber || `#${payment.invoice?.id || payment.id}`;
      const title = payment.status === "SUCCESS" ? "Invoice paid" : "Payment refunded";
      const customer = payment.invoice?.customerEmail || payment.payerEmail || "Unknown customer";
      const amountLabel = formatAmount(payment.amount, payment.currency);
      const description = payment.status === "SUCCESS"
        ? `${invoiceNumber} - ${customer} - ${amountLabel}`
        : `${invoiceNumber} - ${amountLabel}`;
      activity.push({
        id: `payment-${payment.id}-${payment.status}`,
        type: "payment",
        title,
        description,
        amount: payment.amount,
        currency: payment.currency,
        status: payment.status,
        timestamp: resolvePaymentDate(payment),
        meta: {
          invoiceNumber: payment.invoice?.invoiceNumber,
          customerEmail: payment.invoice?.customerEmail || payment.payerEmail,
          transactionId: payment.transactionId,
        },
      });
    }
  });

  subscriptions.forEach((subscription) => {
    if (subscription.status === "ACTIVE" || subscription.status === "CANCELED") {
      const title = subscription.status === "ACTIVE" ? "Subscription active" : "Subscription canceled";
      activity.push({
        id: `subscription-${subscription.id}-${subscription.status}`,
        type: "subscription",
        title,
        description: `${subscription.customer?.email || "Unknown customer"} - ${subscription.plan?.name || "Plan"}`,
        status: subscription.status,
        timestamp: resolveSubscriptionDate(subscription),
        meta: {
          stripeSubscriptionId: subscription.stripeSubscriptionId,
          stripeCustomerId: subscription.customer?.stripeCustomerId,
          customerEmail: subscription.customer?.email,
        },
      });
    }
  });

  subscriptionInvoices.forEach((invoice) => {
    if (invoice.status === "PAID") {
      const amountLabel = formatAmount(invoice.amountPaid, invoice.currency);
      activity.push({
        id: `subscription-invoice-${invoice.id}`,
        type: "subscription_invoice",
        title: "Subscription invoice paid",
        description: `${invoice.externalInvoiceId} - ${amountLabel}`,
        amount: invoice.amountPaid,
        currency: invoice.currency,
        status: invoice.status,
        timestamp: resolveSubscriptionInvoiceDate(invoice),
        meta: {
          stripeSubscriptionId: invoice.stripeSubscriptionId,
          customerEmail: invoice.customerEmail,
        },
      });
    }
  });

  return activity.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
};

export const buildDashboardData = (paymentsHistory, subscriptions, subscriptionInvoices) => ({
  kpis: buildDashboardKpis(paymentsHistory, subscriptions, subscriptionInvoices),
  alerts: buildAlerts(paymentsHistory, subscriptions),
  activity: buildActivityFeed(paymentsHistory, subscriptions, subscriptionInvoices),
});

export const getTimestamp = (value) => parseDateValue(value);
